/**
 * Cache manager that uses a version-based approach to invalidate browser cache
 * when app updates happen.
 */

import { APP_VERSION } from './version';

// A key for localStorage to track the version the user has
const VERSION_KEY = 'app_version';

/**
 * Checks if the app version has changed and clears cache if needed
 * Should be called early in app initialization
 */
export const validateAppCache = (): void => {
  const lastVersion = localStorage.getItem(VERSION_KEY);
  
  // If version doesn't match or doesn't exist, clear caches and update version
  if (lastVersion !== APP_VERSION) {
    // Clear all caches using the Cache API
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }
    
    // Clear application storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Set the new version
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    
    // Force reload if this isn't the first visit (if lastVersion exists)
    if (lastVersion) {
      window.location.reload();
    }
  }
};

/**
 * Force update all caches regardless of version
 * This can be called manually when you know you've made a major update
 * that requires all users to refresh their cache
 */
export const forceUpdateCache = (): void => {
  // Update service worker if available
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then(registration => {
      if (registration) {
        // Send message to service worker to update cache
        registration.update();
        
        // Communicate with the service worker
        if (registration.active) {
          registration.active.postMessage({
            type: 'SET_VERSION',
            version: APP_VERSION
          });
        }
      }
    });
  }
  
  // Clear browser caches
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    });
  }
  
  // Update stored version
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  
  // Reload the page to apply changes
  window.location.reload();
}; 